import _ from 'lodash'
import postcss from 'postcss'
import nestedPostCssPlugin from 'postcss-nested'
// @ts-ignore
import discardEmptyRulesPostCssPlugin from 'postcss-discard-empty'
import {
	Component,
	InnerVariant,
	NthChild,
	OuterVariant,
	RepeaterVariant,
	ResolvedRefArray,
	ResolvedVariantRelation,
	TransformData,
	TransitionData,
	VARIANTS_CSS_STRATEGIES,
	VariantsExceptBreakpoint,
	ResolvedPattern,
	Pattern,
	ResolvedPatternsData,
	ResolvedRepeaterVariant,
	VariantsDataMap,
	ResolvedStyleRefArray,
	BaseDataItem,
} from '@wix/thunderbolt-becky-types'
import {
	compIdToCssSelector,
	getDisplayedId,
	getItemId,
	getRefCompIdFromInflatedId,
	getUniqueId as getRefUniqueId,
	isDisplayedOnly,
} from '@wix/thunderbolt-commons'
import { ESCAPING_CSS_PREFIX, ESCAPING_CSS_SUFFIX, variantSeparator } from './constants'

import { getCSSFromTransitionItem } from '../transitionFunctionLibrary'
import { getCSSFromTransformationItem } from '../transformationsFunctionLibrary'
import type { CompIdToCssAndBreakpoint, CssAndBreakpointId, DataType } from '../../types'
import { removeHash } from '../../functionLibrary'

type VariantCssOptions = {
	componentDefaultRotation?: number
	childrenSelectorStrategy?: string
	dataType: DataType
	isStylableComponent?: boolean
	isRepeaterChild?: boolean
	shouldAddIdSelector?: boolean
	isHoverBoxChild?: boolean
	ignoredTransformProps?: Array<string>
	isEditor: boolean
	containerIdsWithHiddenAndHoverChildren?: Array<string>
	layoutSelectorType?: string
	shouldOmitWrapperLayers?: boolean
	isMobileView: boolean
	ancestors?: Array<string>
}

export type GetCssWithVariantsParams = {
	cssValue: string
	variants?: Array<VariantsExceptBreakpoint>
	compId: string
	options: VariantCssOptions
}

const innerConditionTypes = ['Hover', 'Class', 'State', 'Selected', 'Complex', 'RepeaterPattern']
const notScreenInAnimationSelector = ':not(.is-animating)'
const mobileClassSelector = '.device-mobile-optimized'
const patternsPseudoPlaceHolder = '#pattern_place_holder#'
const patternsPseudoReplaceRgx = new RegExp(patternsPseudoPlaceHolder, 'g')

const getRegularIdSelector = (compId: string) => `#${compId}`
const getTemplateRepeaterIdSelector = (compId: string) => `[id^="${compId}__"]`
const getInflatedRepeaterIdSelector = (compId: string) => `[id="${compId}"]`

const cssPlainRegex = /(\/\*__css\()(.*?)(\)__css\*\/)/

const breakpointIdToCss = (cssAndBreakpointIdArray: Array<CssAndBreakpointId>) => {
	return cssAndBreakpointIdArray.reduce((acc: Record<string, string>, val) => {
		if (acc[val.breakpointId]) {
			acc[val.breakpointId] += `\n${val.css}`
		} else {
			acc[val.breakpointId] = val.css
		}

		return acc
	}, {})
}

const getHoverChildSelector = (compId: string) => {
	return compIdToCssSelector(compId, false)
}

const getIdSelector = (
	compId: string,
	{ isRepeaterChild = false, shouldAddIdSelector = true, isHoverBoxChild = false }: Partial<VariantCssOptions>
) => {
	if (!shouldAddIdSelector) {
		return ''
	}

	if (isHoverBoxChild) {
		return getHoverChildSelector(compId)
	}

	// TB-2804
	// We get both inflated and template child of repeater.
	// We need them to have the same "strength", so we use id=displayedId instead of #displayedId
	// for both compSpecificCss and EffectStyle.
	const isInflatedChildOfRepeater = isDisplayedOnly(compId)
	if (isInflatedChildOfRepeater) {
		return getInflatedRepeaterIdSelector(compId)
	}

	if (isRepeaterChild) {
		return getTemplateRepeaterIdSelector(compId)
	}

	return getRegularIdSelector(compId)
}

const addLayoutSelectorType = (
	compId: string,
	selector: string,
	layoutSelectorType: string = '',
	shouldOmitWrapperLayers: boolean = false
) => {
	if (layoutSelectorType) {
		if (layoutSelectorType === 'component::before') {
			selector += '::before'
		} else if (layoutSelectorType === 'component-one-cell-grid') {
			selector += `:not(.${compId}-container)`
		} else if (layoutSelectorType !== 'component' && layoutSelectorType !== 'item') {
			if (shouldOmitWrapperLayers) {
				if (selector === getRegularIdSelector(compId)) {
					selector = `.${compId}-${layoutSelectorType}`
				}
			} else {
				selector += ` .${compId}-${layoutSelectorType}`
			}
		}
	}

	return selector
}

const getSelectorByVariant = (variant: VariantsExceptBreakpoint, isMobileView: boolean): string => {
	let selector = ''
	switch (variant.type) {
		case 'Hover':
			selector = isMobileView ? `.${variant.id}` : ':hover'
			break
		case 'Preset':
		case 'Class':
		case 'State':
		case 'Selected':
			selector = `.${variant.id}`
			break
		case 'Mobile':
			selector = mobileClassSelector
			break
		case 'Complex':
			selector = variant.variants.reduce(
				(acc, variantInComplex) => (acc += `${getSelectorByVariant(variantInComplex, isMobileView)}`),
				selector
			)
			break
		default:
			// no matching type, throwing
			// @ts-ignore
			throw new Error(`Variant condition does not exist. type: ${variant.type}`)
	}

	if (variant.isNot) {
		selector = `:not(${selector})`
	}

	return selector
}

const getInflatedRepeaterId = (compIdToFix: string, containerId: string) => {
	const itemId = getItemId(containerId)
	if (itemId) {
		compIdToFix = getDisplayedId(compIdToFix, itemId)
	}
	return compIdToFix
}
const getRefInflatedId = (compIdToFix: string, containerId: string) => {
	const refId = getRefCompIdFromInflatedId(containerId)
	const refIdCompTofix = getRefCompIdFromInflatedId(compIdToFix)
	if (refId !== containerId && refId !== refIdCompTofix) {
		return getRefUniqueId(refId, compIdToFix)
	}
	return compIdToFix
}

const fixDisplayedOnlyCompId = (compIdToFix: string, containerId: string) => {
	const repeaterInflatedId = getInflatedRepeaterId(compIdToFix, containerId)
	return getRefInflatedId(repeaterInflatedId, containerId)
}

const handleTransitionStrategy = (selector: string, options: VariantCssOptions) => {
	const isAllChildren = options.childrenSelectorStrategy === VARIANTS_CSS_STRATEGIES.ALL_CHILDREN
	return isAllChildren
		? `${selector}, ${selector} ${notScreenInAnimationSelector} `
		: `${selector}, ${selector} > ${notScreenInAnimationSelector}`
}

const getCssValueByDataType = (itemWithConditions: TransformData | TransitionData, options: VariantCssOptions) => {
	switch (options.dataType) {
		case 'TransformData':
			return getCSSFromTransformationItem(
				itemWithConditions as TransformData,
				options.ignoredTransformProps!,
				options.componentDefaultRotation
			)
		case 'TransitionData':
			return getCSSFromTransitionItem(itemWithConditions as TransitionData)
		default:
			return ''
	}
}

const hoverSelectorRegex = new RegExp(':hover', 'g')

const addFocusA11yRule = (selector: string) => {
	const focusRule = selector.replace(hoverSelectorRegex, ':focus-within')
	return `${selector},.keyboard-tabbing-on ${focusRule}`
}

const addClassRule = (selector: string, variantId: string) => {
	const classRule = selector.replace(hoverSelectorRegex, `.${variantId}`)
	return `${selector}, ${classRule}`
}

const appendAdditionalSuffixesToSelectorIfNeeded = (
	compId: string,
	selector: string,
	targetIdToVariants: Record<string, Array<InnerVariant>>,
	currentTargetId: string,
	patternsVariants: Array<ResolvedRepeaterVariant>,
	isEntireSelectorWithZeroSpecificity: boolean,
	{
		isEditor,
		containerIdsWithHiddenAndHoverChildren = [],
		layoutSelectorType,
		shouldOmitWrapperLayers,
	}: Partial<VariantCssOptions>
) => {
	selector = addPatterns(selector, patternsVariants, isEntireSelectorWithZeroSpecificity)

	selector = addLayoutSelectorType(compId, selector, layoutSelectorType, shouldOmitWrapperLayers)

	const hoverVariant = targetIdToVariants[currentTargetId].find((v) => v.type === 'Hover')

	if (hoverVariant) {
		// isEditor = true means we are NOT in preview nor public
		if (isEditor) {
			return addClassRule(selector, hoverVariant.id)
		} else if (containerIdsWithHiddenAndHoverChildren.includes(hoverVariant.componentId)) {
			return addFocusA11yRule(selector)
		}
	}
	return selector
}

const lowerCssSelectorSpecificity = (
	fullSelector: string,
	selector: string,
	regexPlaceholder: RegExp,
	isEntireSelectorWithZeroSpecificity?: boolean
) => {
	const selectorsSplitPerComp = fullSelector.split(' ')
	return isEntireSelectorWithZeroSpecificity
		? selectorsSplitPerComp
				.map((pseudoSelector) =>
					regexPlaceholder.test(pseudoSelector)
						? `:where(${pseudoSelector.replace(regexPlaceholder, selector)})`
						: pseudoSelector
				)
				.join(' ')
		: fullSelector.replace(regexPlaceholder, `:where(${selector})`)
}
/** *
 * Add patterns duplicate the entire selector per nthChild.
 * Result for example: #mediaContainer-123:nth-child(3n+1), #mediaContainer-123:nth-child(3n+2) {...styleForA }
 */
const addPatterns = (
	_selector: string,
	patternsVariants: Array<ResolvedRepeaterVariant>,
	isEntireSelectorWithZeroSpecificity: boolean
) => {
	if (!patternsVariants.length) {
		return _selector.replace(patternsPseudoReplaceRgx, '')
	}

	return patternsVariants.reduce(
		(acc: string, variant) =>
			acc +
			variant.nthChildren.reduce((accSelector: string, nthChild: NthChild) => {
				const offset = nthChild.offset ? `+${nthChild.offset}` : ''
				const patternSelector = `:nth-child(${nthChild.repeat}n${offset})`
				const selectorWithPattern = lowerCssSelectorSpecificity(
					_selector,
					patternSelector,
					patternsPseudoReplaceRgx,
					isEntireSelectorWithZeroSpecificity
				)
				return [accSelector, selectorWithPattern].filter((str) => str).join(',')
			}, ''),
		''
	)
}

const splitPatternsFromVariants = (variants: Array<InnerVariant>) =>
	_.partition(variants, (variant) => variant.type === 'RepeaterPattern') as [
		Array<ResolvedRepeaterVariant>,
		Array<Exclude<InnerVariant, ResolvedRepeaterVariant>>
	]

const getCssWithInnerVariants = (
	targetIdToVariants: Record<string, Array<InnerVariant>>,
	orderedTargetIds: Array<string>,
	cssValue: string,
	compId: string,
	options: VariantCssOptions
) => {
	// this recursion creates scss string which we then is used to generate regular css
	const getCssRec = (targetIds: Array<string>): string => {
		if (!targetIds.length) {
			return cssValue
		}
		const currentTargetId = targetIds.shift()!

		const [patternsVariants, currentVariants] = splitPatternsFromVariants(targetIdToVariants[currentTargetId])
		const compHasOnlyPatternsVariants = patternsVariants.length > 0 && currentVariants.length === 0
		const variantsOnAncestorComp = currentTargetId !== compId
		const isEntireSelectorWithZeroSpecificity = compHasOnlyPatternsVariants && variantsOnAncestorComp
		const pseudoSelector = currentVariants
			.map((variant) => getSelectorByVariant(variant, options.isMobileView))
			.join('')

		const fixedCurrentTargetId = patternsVariants.length
			? currentTargetId
			: fixDisplayedOnlyCompId(currentTargetId, compId)

		let selector = `${getIdSelector(fixedCurrentTargetId, options)}${patternsPseudoPlaceHolder}${pseudoSelector}`

		if (!targetIds.length) {
			// if none of the condition include the comp id we need to select it using #compId
			// stylable components don't need to be targeted with #compId since it has unique class names so we can
			// reduce specifity of the interaction rule to support interactions on stylable components
			if (
				fixDisplayedOnlyCompId(currentTargetId, compId) !== compId &&
				(!options.isStylableComponent || isEntireSelectorWithZeroSpecificity)
			) {
				selector = `${selector} ${getIdSelector(compId, options)}`
			}

			// if we are in Transition style we need to select all children or only direct children to be affected.
			if (options.dataType === 'TransitionData') {
				selector = handleTransitionStrategy(selector, options)
			}

			return `${appendAdditionalSuffixesToSelectorIfNeeded(
				compId,
				selector,
				targetIdToVariants,
				currentTargetId,
				patternsVariants,
				isEntireSelectorWithZeroSpecificity,
				options
			)} { ${cssValue} }`
		}

		return `${appendAdditionalSuffixesToSelectorIfNeeded(
			fixedCurrentTargetId,
			selector,
			targetIdToVariants,
			currentTargetId,
			patternsVariants,
			isEntireSelectorWithZeroSpecificity,
			options
		)} { ${getCssRec(targetIds)} }`
	}

	return getCssRec(orderedTargetIds)
}

const getCssWithOuterVariants = (
	outerConditions: Array<OuterVariant>,
	innerVariantsCss: string,
	isMobileView: boolean
) => {
	const outerCondition = outerConditions[0] // Can it be multiple conditions?
	const outerSelector = getSelectorByVariant(outerCondition, isMobileView)
	return `${outerSelector} { ${innerVariantsCss} }`
}

export const generateCss = (nestedCss: string): string => {
	try {
		return postcss([nestedPostCssPlugin, discardEmptyRulesPostCssPlugin]).process(nestedCss, {
			map: false,
			from: undefined,
		}).css
	} catch {
		// TODO: report error in css (#TB-1988)
		return nestedCss
	}
}

const splitConditions = (variants: Array<VariantsExceptBreakpoint>): [Array<InnerVariant>, Array<OuterVariant>] =>
	// @ts-ignore
	_.partition(variants, (variant) => innerConditionTypes.includes(variant.type))

const getTargetIds = (targetId: string, innerConditions: Array<InnerVariant>, { ancestors }: VariantCssOptions) => {
	const targetIds = _.chain(innerConditions)
		.map((variant) => variant.componentId)
		.uniq()
		.value()

	const hasOnlyOneTargetId = targetIds.length <= 1
	if (hasOnlyOneTargetId || !ancestors) {
		return targetIds
	}
	const hasVariantOnTarget = ancestors[0] !== targetId
	if (hasVariantOnTarget) {
		ancestors.unshift(targetId)
	}

	return ancestors.filter((ancestorId) => targetIds.includes(ancestorId)).reverse()
}

const getInnerConditionsGroupByTarget = (innerConditions: Array<InnerVariant>) =>
	innerConditions.reduce((targetIdToVariants, variant) => {
		targetIdToVariants[variant.componentId] = [...(targetIdToVariants[variant.componentId] || []), variant]
		return targetIdToVariants
	}, {} as Record<string, Array<InnerVariant>>)

const extractEscapedCss = (css: string) => {
	if (!css) {
		return { escapedCss: null }
	}
	const regex = new RegExp(cssPlainRegex)
	// remove all line breaks
	const cssValue = css.replace(/(\r\n|\n|\r)/gm, '')
	const match = regex.exec(cssValue)
	if (!match) {
		return { escapedCss: null }
	}
	// getting the plain css and remove the escaping
	const plainCss = match[0]
	const escapedCss = plainCss.replace(ESCAPING_CSS_PREFIX, '').replace(ESCAPING_CSS_SUFFIX, '')
	// get all css attributes without the plainCss
	const nonEscapedCss = cssValue
		.split(plainCss)
		.map((cssAttribute) => (cssAttribute.startsWith(';') ? cssAttribute.substring(1) : cssAttribute))
		.join('')
	return {
		escapedCss,
		nonEscapedCss,
	}
}

const getDefaultCss = (cssValue: string, compId: string, options: VariantCssOptions) => {
	let selector = getIdSelector(compId, options)
	selector = addLayoutSelectorType(compId, selector, options.layoutSelectorType, options.shouldOmitWrapperLayers)

	// We disable default transform-origin and transition (and not componentStyle) when there is active screen-in animation
	// since they are affecting the animation
	if (
		options.dataType === 'TransitionData' ||
		(options.dataType === 'TransformData' && cssValue.includes('transform-origin'))
	) {
		// NOTE: if we will have both transforms and transform-origin in the default item we will need special handling here.
		selector = `${selector}${notScreenInAnimationSelector}`
	}

	// For transition data only
	const isDirectChildrenOnly = options.childrenSelectorStrategy === VARIANTS_CSS_STRATEGIES.DIRECT_CHILDREN
	if (isDirectChildrenOnly) {
		selector = `${selector}, ${selector} > ${notScreenInAnimationSelector}`
	}

	let defaultCss = `${selector} { ${cssValue} }`

	const { escapedCss, nonEscapedCss } = extractEscapedCss(cssValue)
	if (escapedCss) {
		defaultCss = `${selector} { ${nonEscapedCss} }`
	}

	if (options.childrenSelectorStrategy === VARIANTS_CSS_STRATEGIES.ALL_CHILDREN) {
		// #TB-5244
		// We need to disable transition of visibility property for all skin parts because it happens in serial
		defaultCss += `${selector} ${notScreenInAnimationSelector} { ${cssValue}, visibility 0s }`
	}

	const res = generateCss(defaultCss)

	return escapedCss ? `${escapedCss} ${res}` : res
}

const getCssUnderVariants = (
	variants: Array<VariantsExceptBreakpoint>,
	cssValue: string,
	compId: string,
	options: VariantCssOptions
) => {
	const [innerConditions, outerConditions] = splitConditions(variants)
	const targetIds: Array<string> = getTargetIds(compId, innerConditions, options)
	// this grouping is needed in order to combine multiple condition on same target (#container:hover.class)
	const groupedInnerVariants = getInnerConditionsGroupByTarget(innerConditions)

	const innerVariantsCss = innerConditions.length
		? getCssWithInnerVariants(groupedInnerVariants, targetIds, cssValue, compId, options)
		: `${addLayoutSelectorType(
				compId,
				getIdSelector(compId, options),
				options.layoutSelectorType,
				options.shouldOmitWrapperLayers
		  )} { ${cssValue} }` // Only outer variants, need to select to compId

	const variantsCss = outerConditions.length
		? getCssWithOuterVariants(outerConditions, innerVariantsCss, options.isMobileView)
		: innerVariantsCss

	return generateCss(variantsCss)
}

const getCssWithVariants = ({ cssValue, variants, compId, options }: GetCssWithVariantsParams) => {
	if (variants?.length) {
		return getCssUnderVariants(variants, cssValue, compId, options)
	} else {
		return getDefaultCss(cssValue, compId, options)
	}
}

const getCssWithVariantsSelector = (params: GetCssWithVariantsParams) => {
	const css = getCssWithVariants(params)
	// Taking only selector
	return css.split(' {').shift()
}

const getStyleGroupedByBreakpointId = (compIdToCssAndBreakpoint: CompIdToCssAndBreakpoint) => {
	const groupedBy = _.groupBy(Object.values(compIdToCssAndBreakpoint), 'breakpointId')
	const breakpointIdToCSS = Object.entries(groupedBy).reduce((accu, [breakPointId, cssAndBreakpointArray]) => {
		accu[breakPointId] = cssAndBreakpointArray.map(({ css }) => css).join('')
		return accu
	}, {} as Record<string, string>)

	return breakpointIdToCSS
}

const getCompVariants = (
	compIdToStructure: { [compId: string]: Component },
	variants: VariantsDataMap,
	styleData: ResolvedStyleRefArray
) => {
	const [compId] = Object.keys(compIdToStructure)
	if (!compId.includes(variantSeparator)) {
		return []
	}
	const [, styleId] = compId.split(variantSeparator)
	const styleWithVariantRelation = Object.values(styleData.values).find((style) => style.id === styleId)
	return (
		(styleWithVariantRelation as ResolvedVariantRelation<BaseDataItem<'TopLevelStyle' | 'ComponentStyle'>>)
			?.variants || []
	)
}

// VARIANTS has stronger selectors by browser specificity, so rendering it first shouldn't affect the winning style,
// except for the following use case described here - TB-5537, in this specific use case, we want the DEFAULT style to "win"
// Therefore the DEFAULT is rendered after "VARIANTS".
// REPEATER_VARIANT gets ":where" selector which makes it without any score, so it has the same specificity as DEFAULT style,
// but since its should override DEFAULT style, the code make sure to render it last.
const refArrayOrder = {
	VARIANTS: 0,
	DEFAULT: 1,
	REPEATER_VARIANT: 2,
}
export const getVariantsOrderScore = (variantsIds: Array<string>, variants: VariantsDataMap) => {
	if (variantsIds.length === 0) {
		return refArrayOrder.DEFAULT
	}

	const orderScore = variantsIds.reduce((variantsOrderScore, variantIdWithHash) => {
		const variantId = removeHash(variantIdWithHash) as string
		// delete defensive check when https://jira.wixpress.com/browse/TB-7305 is fixed
		if (variants[variantId]?.type === 'RepeaterPattern') {
			variantsOrderScore = variantsOrderScore === 0 ? 0 : refArrayOrder.REPEATER_VARIANT
		} else if (variants[variantId] && variants[variantId]?.type !== 'BreakpointRange') {
			variantsOrderScore = refArrayOrder.VARIANTS
		}
		return variantsOrderScore
	}, 1)
	return orderScore
}

/**
 * This method sorts an array of component structures with its variants so that the variant structures
 * will be first in order.
 */
const sortComponentStructuresWithVariants = (
	array: Array<{ [compId: string]: Component }>,
	variants: VariantsDataMap,
	styleData: ResolvedStyleRefArray
) =>
	[...array].sort((compIdToStructureA, compIdToStructureB) => {
		const variantsIdsInA = getCompVariants(compIdToStructureA, variants, styleData)
		const variantsIdsInB = getCompVariants(compIdToStructureB, variants, styleData)
		const variantsOrderScoreInA = getVariantsOrderScore(variantsIdsInA, variants)
		const variantsOrderScoreInB = getVariantsOrderScore(variantsIdsInB, variants)
		const variantsLengthInA = variantsIdsInA.length
		const variantsLengthInB = variantsIdsInB.length

		if (variantsOrderScoreInA === variantsOrderScoreInB) {
			if (variantsLengthInA !== variantsLengthInB) {
				return variantsLengthInA < variantsLengthInB ? -1 : 1
			}
			// maintain the order for when there is no difference
			return 0
		}

		return variantsOrderScoreInA < variantsOrderScoreInB ? -1 : 1
	})

const getVariantIdsForHiddenContentTransformations = (transformationData: ResolvedRefArray<TransformData>) => {
	if (transformationData.values.length === 1) {
		return
	}

	const [defaultState, ...variants] = transformationData.values
	const attributesToCheckZero = ['opacity', 'scale.x', 'scale.y']
	const attributesToCheckChange = ['translate.x.value', 'translate.y.value']
	const attributesWithZero = attributesToCheckZero.filter((attribute) => _.get(defaultState, attribute) === 0)

	const variantIdsThatRevealContent = attributesWithZero.map((attribute) =>
		((variants as unknown) as Array<ResolvedVariantRelation<TransformData>>)
			.filter((variantRelation) => _.get(variantRelation.to, attribute) >= 1)
			.map((variantRelation) => variantRelation.variants)
	)
	const variantIdsThatMoveContent = attributesToCheckChange.map((attribute) =>
		((variants as unknown) as Array<ResolvedVariantRelation<TransformData>>)
			.filter((variantRelation) => _.get(variantRelation.to, attribute) !== _.get(defaultState, attribute))
			.map((variantRelation) => variantRelation.variants)
	)

	return _.flattenDeep(variantIdsThatRevealContent.concat(variantIdsThatMoveContent))
}

const patternToNthChildren = ({
	patternId,
	variant,
	patternsData,
	// Remove this flag once we merge specs.thunderbolt.useRuntimeDataSchemasV2
	isResolvedPatternsData,
}: {
	patternId: string
	variant: RepeaterVariant
	patternsData: ResolvedPatternsData
	isResolvedPatternsData: boolean
}) => {
	if (!patternsData[patternId]) {
		return []
	}

	const patternItems = isResolvedPatternsData
		? (patternsData[patternId] as ResolvedPattern).items
		: ((patternsData[patternId] as any) as Pattern).items.map(
				(nthChildId) => patternsData[removeHash(nthChildId)!] as NthChild
		  )

	return patternItems.reduce<Array<NthChild>>((filteredItems, nthChild) => {
		if (removeHash(nthChild.variant) === variant.id) {
			filteredItems.push(nthChild)
		}

		return filteredItems
	}, [])
}

export {
	getCssWithVariantsSelector,
	getCssWithVariants,
	getCssValueByDataType,
	breakpointIdToCss,
	getStyleGroupedByBreakpointId,
	sortComponentStructuresWithVariants,
	getVariantIdsForHiddenContentTransformations,
	patternToNthChildren,
}
